import { phoneClock, screenLocale } from '../round-config'
import '../chats.css'
import { ApiError, api, changeNow, getQueue, mutate } from '../api'
import { roundMessageBy } from '../contact'
import { saveWay, wayIcon, type WayCustomer } from '../contact-ui'
import { enquiryFirstMessage, enquirySecondMessage, quoteNudgeMessage } from '../enquiry-message'
import { buzz } from '../haptic'
import { smsHref } from '../late-message'
import { mailtoHref } from '../mailto'
import { bindEmailSend, sendingSettings } from '../email-send'
import { onAPhone, whatsappHref } from '../rebook-message'
import { routeGen } from '../route-state'
import { clear, el, money, shortDate, telHref } from '../ui'
import { getMe, loadMe } from '../me'
import { offerUndo } from '../undo'

// Chats (Liam, 10 Oct 2026: "a whatsapp style ... messaging service in the app to talk to customers from there all in
// one so its easy to carry on conversations", then "Do it" to https://claude.ai/artifact/6idhg1JZyDLjy7LxESDYMM).
// Opened from Messages through the top-bar Chats button: every conversation with a customer or an enquiry, newest first, unread on
// top of each; one conversation as bubbles (theirs white, yours lemon) with how each went; a reply box that sends the way
// they last wrote unless another is picked. Customers never log in to anything: Roundly sends texts and WhatsApp from the
// business's Twilio number and emails whose replies come back here (functions/lib/chat.ts, chat-send.ts). Where Roundly
// cannot send a way itself, the phone's own app opens with the words written and the message is kept as sent from it.
// A phone shows the list, then one conversation; a computer shows both side by side.

type Kind = 'lead' | 'customer'
type Way = 'whatsapp' | 'text' | 'email'
interface ChatRow { kind: Kind; id: number; name: string; sub: string; last: { direction: string; way: string; body: string | null; state: string; at: string; read?: boolean }; unread: number }
// WhatsApp through Meta (0081; docs/plans/whatsapp-direct/PLAN.md, pictures https://claude.ai/artifact/PdC9wPAwE4rkQepWP8LXFz):
// a message says where it came from (source 'phone': typed on the work phone; 'history': brought in from before
// connecting), its photo or voice note (media_type, media_state; the bytes at /api/chats/media/:id), a voice note written
// out (words), and unsure when Roundly couldn't hear back whether it went.
export interface Msg {
  id: number; direction: 'in' | 'out'; way: string; body: string | null; state: string; at: string; read_at?: string | null; pending?: boolean
  // The key it was sent under: ties It arrived to the very send held in this chat, never to another with the same words.
  send_key?: string | null
  source?: 'phone' | 'history' | null; media_type?: string | null; media_state?: string | null; words?: string | null; unsure?: number | null; chase_send?: string | null
}
interface Ways {
  text: boolean; whatsapp: boolean; whatsappFree: boolean; templates: { first: boolean; nudge: boolean }; email: boolean; mobile: boolean
  whatsappBy?: 'meta' | 'twilio' | null; waUntil?: string | null; waStop?: { state: 'stopped' | 'held'; at: string; words: string | null } | null; waProblem?: string | null
}
export interface Thread {
  who: { kind: Kind; id: number; name: string; phone: string | null; email: string | null; address: string | null; sub: string; messageBy: 'whatsapp' | 'text' | 'email' | 'none' | null; version?: number; quoted?: boolean }
  messages: Msg[]; ways: Ways; more?: boolean; owes?: { visits: number; pence: number; oldest: string | null } | null
}

const QUICK = ['On my way', 'Running about 20 minutes late, sorry!', 'Thank you!']
// An enquiry's quick fills (Liam, 15 Oct 2026: "my quick fill options are get in touch / first contact messages we would
// normally have"): the round's own words from Your messages with their name in, the same as Messages and New jobs send.
// The nudge is about a quote, so it shows once they have one. A customer keeps the day's quick fills.
function quickFills(t: Thread): { label: string; words: string }[] {
  if (t.who.kind !== 'lead') return QUICK.map((q) => ({ label: q, words: q }))
  return [
    { label: 'First reply', words: enquiryFirstMessage(t.who.name) },
    { label: 'Second message', words: enquirySecondMessage(t.who.name) },
    ...(t.who.quoted ? [{ label: 'Gentle nudge', words: quoteNudgeMessage(t.who.name) }] : []),
  ]
}
const POLL_MS = 15_000

let list: ChatRow[] | null = null
let listError = ''
let filter: 'all' | 'unread' | 'lead' | 'customer' = 'all'
let search = ''
let open: { kind: Kind; id: number } | null = null
let thread: Thread | null = null
let threadError = ''
let draft = ''
let picked: Way | null = null
let sending = false
let sendError = ''
let pending: Msg[] = []
let pollTimer: number | null = null
let listSig = ''
let threadSig = ''
// The last message id this conversation was marked read up to (peer review: only what was shown, every time new ones
// come in, never skipped by a poll that found nothing else new).
let seenSent = 0
// One request key per message being sent, kept until the server answers, so tapping Send again after a lost signal can
// never send it twice (peer review).
let sendKey: string | null = null
// A send that couldn't be confirmed (no signal) keeps its words and its key with ITS chat, never another's: moving to
// another chat and back finds them there, and the next Send reuses the key so it can never go twice (GPT-6.1's WhatsApp
// review: a late failure put the words in the chat now open, and editing them dropped the key).
const unsure = new Map<string, { words: string; key: string }>()
// A send still on its way, by chat: while it is, that chat's Send waits, and coming back to the chat never refills the box.
const inflight = new Set<string>()
const chatKey = (o: { kind: string; id: number } | null): string => (o ? `${o.kind}:${o.id}` : '')
// "Earlier messages" (item 63): older messages loaded above the latest 300, kept while this conversation stays open.
let earlier: Msg[] = []
let earlierMore: boolean | null = null

const phoneLayout = (): boolean => typeof window.matchMedia === 'function' && window.matchMedia('(max-width: 760px)').matches
const onChats = (): boolean => window.location.hash.startsWith('#/chats')
const sameChat = (a: { kind: Kind; id: number } | null, b: { kind: Kind; id: number } | null): boolean => !!a && !!b && a.kind === b.kind && a.id === b.id

// "2026-10-10 11:42:05" (UTC, as the server keeps it) → this phone's clock.
const when = (at: string): Date => new Date(`${at.replace(' ', 'T')}Z`)
const dayKey = (d: Date): string => `${d.getFullYear()}-${d.getMonth()}-${d.getDate()}`
function dayWords(d: Date): string {
  const now = new Date(), y = new Date(now.getTime() - 86_400_000)
  if (dayKey(d) === dayKey(now)) return 'Today'
  if (dayKey(d) === dayKey(y)) return 'Yesterday'
  return d.toLocaleDateString(screenLocale(), { weekday: 'short', day: 'numeric', month: 'short' })
}
const clock = (d: Date): string => phoneClock(d)
function listTime(at: string): string {
  const d = when(at), days = (Date.now() - d.getTime()) / 86_400_000
  const words = dayWords(d)
  if (words === 'Today') return clock(d)
  if (words === 'Yesterday') return words
  return days < 6 ? d.toLocaleDateString('en-GB', { weekday: 'short' }) : d.toLocaleDateString(screenLocale(), { day: 'numeric', month: 'short' })
}
const initials = (name: string): string => name.trim().split(/\s+/).slice(0, 2).map((s) => s[0] ?? '').join('').toUpperCase()
const WAY_WORDS: Record<string, string> = { whatsapp: 'WhatsApp', text: 'Text', email: 'Email', call: 'Call', form: 'Website form' }

// Ticks, as WhatsApp shows them (Liam, 10 Oct 2026: "Should there be two ticks if you can verify sent?", "Yes"), only for
// what Roundly can verify: one grey tick sent, two grey delivered, two green read (WhatsApp, read receipts on); a small
// phone for a message opened in the phone's own app (Roundly cannot see it go); a clock while sending; red ! if it failed.
type Mark = 'pending' | 'opened' | 'sent' | 'delivered' | 'read' | 'failed'
const markOf = (state: string, read: boolean, pending = false): Mark =>
  pending ? 'pending' : state === 'failed' ? 'failed' : read ? 'read' : state === 'delivered' ? 'delivered' : state === 'sent' ? 'sent' : 'opened'
const MARK_WORDS: Record<Mark, string> = { pending: 'Sending', opened: 'Opened on your phone', sent: 'Sent', delivered: 'Delivered', read: 'Read', failed: "Didn't go" }
const MARK_PATHS: Record<Mark, string[]> = {
  pending: ['M12 3a9 9 0 1 0 0 18 9 9 0 0 0 0-18z', 'M12 7v5l3 2'],
  opened: ['M8 3h8a1 1 0 0 1 1 1v16a1 1 0 0 1-1 1H8a1 1 0 0 1-1-1V4a1 1 0 0 1 1-1z', 'M11 18h2'],
  sent: ['M4 12.5l4.5 4.5L19 7'],
  delivered: ['M2 12.5l4.5 4.5L16 7', 'M10 16l1 1L21.5 7'],
  read: ['M2 12.5l4.5 4.5L16 7', 'M10 16l1 1L21.5 7'],
  failed: ['M12 4v10', 'M12 18.5v.5'],
}
function markIcon(mark: Mark): SVGElement {
  const svg = document.createElementNS('http://www.w3.org/2000/svg', 'svg')
  svg.setAttribute('viewBox', '0 0 24 24'); svg.setAttribute('width', '16'); svg.setAttribute('height', '16')
  svg.setAttribute('role', 'img'); svg.setAttribute('aria-label', MARK_WORDS[mark])
  svg.setAttribute('class', `chat-mark chat-mark-${mark}`)
  for (const d of MARK_PATHS[mark]) {
    const path = document.createElementNS('http://www.w3.org/2000/svg', 'path')
    path.setAttribute('d', d); path.setAttribute('fill', 'none'); path.setAttribute('stroke', 'currentColor'); path.setAttribute('stroke-width', '2.2'); path.setAttribute('stroke-linecap', 'round'); path.setAttribute('stroke-linejoin', 'round')
    svg.append(path)
  }
  return svg
}

// The ways this conversation can go: WhatsApp and Text with a phone number, Email with an email address. A number that
// asked to stop gets no WhatsApp from Roundly at all, neither sent nor opened (0081, item 34).
function waysFor(t: Thread): Way[] {
  const can: Way[] = []
  if (t.who.phone) can.push(...(t.ways.waStop ? [] : ['whatsapp' as const]), 'text')
  if (t.who.email) can.push('email')
  return can
}
// The way it starts on (Liam, 10 Oct 2026: "if they email probably will send whatsapp if they have it"): their own
// choice (a customer's page, an enquiry's arrow, or a pick made here); then the round's usual from Your business when it
// reaches them (Liam, 15 Oct 2026: a round on Text still opened its chats on WhatsApp); otherwise WhatsApp when they
// have a mobile, even if they emailed; email only when there is no mobile. A different way is one tap away, and is
// remembered for them.
function defaultWay(t: Thread): Way {
  const can = waysFor(t)
  const saved = t.who.messageBy
  if ((saved === 'whatsapp' || saved === 'text' || saved === 'email') && can.includes(saved)) return saved
  const round = roundMessageBy()
  if (can.includes(round)) return round
  if (t.ways.mobile && can.includes('whatsapp')) return 'whatsapp'
  if (can.includes('email')) return 'email'
  return can[0] ?? 'whatsapp'
}
// What the Send button does, in its own words: sent by Roundly ("Send text"), or the phone's own app opened with the
// words written ("Open WhatsApp"), so nobody has to read the line under it to know.
function sendLabel(t: Thread, way: Way): string {
  if (roundlySends(t, way)) return way === 'whatsapp' ? 'Send WhatsApp' : way === 'text' ? 'Send text' : 'Send email'
  return way === 'whatsapp' ? 'Open WhatsApp' : way === 'text' ? 'Open Messages' : 'Open email'
}
// How many texts a message is charged as (Twilio: 160 plain letters, 153 each when longer; 70 and 67 once it holds a
// letter texts cannot carry plainly, such as an emoji or a curly ’). About 3p each.
const GSM = /^[@£$¥èéùìòÇ\nØø\rÅåΔ_ΦΓΛΩΠΨΣΘΞÆæßÉ !"#¤%&'()*+,\-./0-9:;<=>?¡A-ZÄÖÑÜ§¿a-zäöñüà^{}\\\[~\]|€]*$/
export function textParts(text: string): number {
  if (!text) return 0
  if (GSM.test(text)) {
    const n = [...text].reduce((k, ch) => k + ('^{}\\[~]|€'.includes(ch) ? 2 : 1), 0)
    return n <= 160 ? 1 : Math.ceil(n / 153)
  }
  const n = [...text].length
  return n <= 70 ? 1 : Math.ceil(n / 67)
}
// Roundly sends this way itself, or opens the phone's own app. WhatsApp through Meta goes from Roundly only inside the 24
// hours after they last wrote; outside them the phone's WhatsApp opens with the words in (item 65).
const roundlySends = (t: Thread, w: Way): boolean =>
  (w === 'email' ? t.ways.email : w === 'text' ? t.ways.text : t.ways.whatsapp && (t.ways.whatsappBy !== 'meta' || t.ways.whatsappFree))

export function mountChats(): void {
  if(!onAPhone()&&getMe()?.role!=='worker')void sendingSettings().catch(()=>{})
  const params = new URLSearchParams(window.location.hash.split('?')[1] ?? '')
  const c = Number(params.get('customer')), l = Number(params.get('lead'))
  const wanted = c > 0 ? { kind: 'customer' as const, id: c } : l > 0 ? { kind: 'lead' as const, id: l } : null
  if (wanted && !sameChat(wanted, open)) {
    open = wanted; thread = null; draft = ''; picked = null; pending = []; sendError = ''; seenSent = 0; sendKey = null; earlier = []; earlierMore = null
    const held = inflight.has(chatKey(wanted)) ? undefined : unsure.get(chatKey(wanted))
    if (held) { draft = held.words; sendKey = held.key; sendError = "We couldn't confirm your last message went. Tap Send again when you're connected: it won't go twice." }
  }
  if (!wanted && phoneLayout()) open = null
  render()
  void loadList()
  if (open) void loadThread()
  if (pollTimer !== null) window.clearInterval(pollTimer)
  const gen = routeGen
  pollTimer = window.setInterval(() => {
    if (gen !== routeGen || !onChats()) { if (pollTimer !== null) window.clearInterval(pollTimer); pollTimer = null; return }
    if (document.visibilityState !== 'visible') return
    void loadList(true)
    if (open && !sending) void loadThread(true)
  }, POLL_MS)
}

export function chatsOnDataChanged(): void {
  void loadList(true)
  if (open && !sending) void loadThread(true)
}

async function loadList(quiet = false): Promise<void> {
  const gen = routeGen
  try {
    const data = await api<{ chats: ChatRow[] }>('/api/chats')
    if (gen !== routeGen || !onChats()) return
    const sig = JSON.stringify(data.chats)
    // A poll that finds nothing new leaves the screen alone (a reply being typed is never disturbed).
    if (quiet && sig === listSig && list) return
    listSig = sig
    list = data.chats
    listError = ''
    // A computer opens the newest conversation when none is chosen, as the drawing shows.
    if (!open && !phoneLayout() && list.length) { open = { kind: list[0]!.kind, id: list[0]!.id }; void loadThread() }
  } catch (err) {
    if (gen !== routeGen || !onChats()) return
    if (!quiet || !list) listError = (err as Error).message || "Chats didn't load."
  }
  render()
}

async function loadThread(quiet = false): Promise<void> {
  const want = open
  if (!want) return
  const gen = routeGen
  try {
    const data = await api<Thread>(`/api/chats/${want.kind}/${want.id}`)
    if (gen !== routeGen || !onChats() || !sameChat(want, open)) return
    // An enquiry that became a customer answers as the customer.
    const who = { kind: data.who.kind, id: data.who.id }
    // Read up to the newest message shown, whenever something new from them has come in.
    const shown = data.messages.reduce((n, m) => Math.max(n, m.id), 0)
    if (data.messages.some((m) => m.direction === 'in' && m.id > seenSent)) {
      seenSent = shown
      const row = list?.find((r) => sameChat(r, who) || sameChat(r, want))
      if (row) row.unread = 0
      void changeNow('POST', `/api/chats/${who.kind}/${who.id}/seen`, { upto: shown }).catch(() => { seenSent = 0 })
    }
    const sig = JSON.stringify([data.messages, data.ways])
    if (quiet && sig === threadSig && thread) return
    threadSig = sig
    const first = !thread
    thread = data
    threadError = ''
    // A phone opens a conversation at its newest message, just above the reply box.
    if (first && phoneLayout()) window.setTimeout(() => window.scrollTo?.({ top: document.body.scrollHeight }), 0)
    if (data.who.kind !== want.kind || data.who.id !== want.id) open = who
  } catch (err) {
    if (gen !== routeGen || !onChats() || !sameChat(want, open)) return
    if (!quiet || !thread) threadError = (err as Error).message || "This conversation didn't load."
  }
  render()
}

function choose(c: { kind: Kind; id: number } | null): void {
  if (sameChat(c, open) && !phoneLayout()) return
  open = c
  thread = null; threadError = ''; draft = ''; picked = null; pending = []; sendError = ''; seenSent = 0; sendKey = null; earlier = []; earlierMore = null
  // Never while its retry is on its way: the box would then hold words that are about to have gone (Astra).
  const held = inflight.has(chatKey(c)) ? undefined : unsure.get(chatKey(c))
  if (held) { draft = held.words; sendKey = held.key; sendError = "We couldn't confirm your last message went. Tap Send again when you're connected: it won't go twice." }
  // The address follows the open conversation without reloading the page (a link or an alert can open it again).
  globalThis.history?.replaceState?.(null, '', c ? `#/chats?${c.kind}=${c.id}` : '#/chats')
  render()
  if (c) void loadThread()
  if (phoneLayout() && typeof window.scrollTo === 'function') window.scrollTo({ top: 0 })
}

// The top-bar Chats button keeps the last known unread-conversation count through failed reads.
export const switchCounts: { toSend: number | null; unread: number | null } = { toSend: null, unread: null }
let countsOwner = ''
export function paintChatsButton(unread: number | null): void {
  const owner = `${getMe()?.businessId}|${getMe()?.email}`
  if (owner !== countsOwner) { switchCounts.unread = null; countsOwner = owner }
  if (unread !== null) switchCounts.unread = unread
  const button = document.querySelector<HTMLElement>('.messages-chats-button')
  if (!button) return
  const count = switchCounts.unread
  button.replaceChildren('Chats')
  if (count !== null && count > 0) button.append(el('span', { class: 'messages-chats-count' }, String(count)))
  button.setAttribute('aria-label', count === null ? 'Chats' : `Chats, ${count} unread`)
}
// The unread number for the switch on Messages (one read, never blocking the page).
export async function chatsUnread(): Promise<number | null> {
  const owner = `${getMe()?.businessId}|${getMe()?.email}`
  try {
    const rows = (await api<{ chats: ChatRow[] }>('/api/chats')).chats
    return owner === `${getMe()?.businessId}|${getMe()?.email}` ? rows.reduce((n, c) => n + (c.unread > 0 ? 1 : 0), 0) : null
  } catch { return null }
}

let pageEl: HTMLElement | null = null
let headingEl: HTMLElement | null = null
function render(): void {
  const root = document.querySelector<HTMLElement>('#screen')
  if (!root || !onChats()) return
  const typing = !!(document.activeElement as HTMLElement | null)?.classList?.contains('chat-input')
  const scroller = root.querySelector<HTMLElement>('.chat-messages')
  const atBottom = !scroller || scroller.scrollHeight - scroller.scrollTop - scroller.clientHeight < 60
  const unread = list ? list.reduce((n, c) => n + (c.unread > 0 ? 1 : 0), 0) : null
  const phone = phoneLayout()
  // The heading is drawn once and kept while Chats is showing, so the switch's slide is never cut short by the list
  // arriving or a poll redrawing; only the counts in it change. Everything below it is drawn afresh.
  let page = pageEl && pageEl.parentElement === root ? pageEl : null
  if (!page) {
    clear(root)
    page = pageEl = el('div', { class: 'chats-page' })
    headingEl = null
    root.append(page)
  }
  page.className = `chats-page${open ? ' chats-has-open' : ''}`
  for (const old of [...page.children]) if (old !== headingEl) old.remove()
  if (!(phone && open)) {
    if (!headingEl) {
      headingEl = el('div', { class: 'messages-heading chats-heading' },
        el('a', { href: '#/rebook', class: 'small-button chats-messages-back' }, '‹ Messages'),
        el('div', { class: 'messages-title' }, el('h1', {}, 'Chats')))
      page.prepend(headingEl)
    }
  } else if (headingEl) {
    headingEl.remove()
    headingEl = null
  }
  paintChatsButton(unread)

  const body = el('div', { class: 'chats-body' })
  if (!(phone && open)) body.append(listPanel())
  if (open) body.append(threadPanel())
  else if (!phone) body.append(el('section', { class: 'chat-thread chat-empty' }, el('p', { class: 'muted' }, 'Pick a conversation.')))
  page.append(body)
  const box = root.querySelector<HTMLElement>('.chat-messages')
  if (box && atBottom) box.scrollTop = box.scrollHeight
  if (typing) root.querySelector<HTMLTextAreaElement>('.chat-input')?.focus({ preventScroll: true })
}

function listPanel(): HTMLElement {
  const panel = el('section', { class: 'chats-list', 'aria-label': 'Chats' })
  const box = el('input', { type: 'search', class: 'search-box chats-search', placeholder: 'Search chats', 'aria-label': 'Search chats' }) as HTMLInputElement
  box.value = search
  box.addEventListener('input', () => { search = box.value; const at = box.selectionStart; render(); const again = document.querySelector<HTMLInputElement>('.chats-search'); again?.focus(); if (again && at !== null) again.setSelectionRange(at, at) })
  panel.append(box)
  const unreadN = list ? list.filter((c) => c.unread > 0).length : 0
  const chips = el('div', { class: 'chats-filters', role: 'group', 'aria-label': 'Show' })
  for (const [k, label] of [['all', 'All'], ['unread', unreadN ? `Unread · ${unreadN}` : 'Unread'], ['lead', 'Enquiries'], ['customer', 'Customers']] as const) {
    const b = el('button', { type: 'button', class: `chats-filter${filter === k ? ' chats-filter-on' : ''}`, 'aria-pressed': String(filter === k) }, label)
    b.addEventListener('click', () => { filter = k; render() })
    chips.append(b)
  }
  panel.append(chips)
  if (listError && !list) { panel.append(el('p', { class: 'empty-note' }, `${listError} Pull down or come back to try again.`)); return panel }
  if (!list) { panel.append(el('p', { class: 'muted' }, 'Getting your chats…')); return panel }
  const q = search.trim().toLowerCase()
  const rows = list.filter((c) => (filter === 'all' || (filter === 'unread' ? c.unread > 0 : c.kind === filter)) && (!q || c.name.toLowerCase().includes(q) || (c.last.body ?? '').toLowerCase().includes(q)))
  if (!rows.length) {
    panel.append(el('p', { class: 'empty-note' }, list.length ? 'No chats match.' : 'No chats yet. When a customer or an enquiry messages you, or you message them from Roundly, the conversation shows here.'))
    return panel
  }
  const ul = el('ul', { class: 'chats-rows' })
  for (const c of rows) {
    const on = sameChat(c, open)
    const mine = c.last.direction === 'out'
    const a = el('button', { type: 'button', class: `chats-row${c.unread ? ' chats-row-unread' : ''}${on ? ' chats-row-on' : ''}`, 'aria-label': `${c.name}${c.unread ? `, ${c.unread} unread` : ''}` })
    if (on) a.setAttribute('aria-current', 'true')
    const tag = c.kind === 'lead' ? el('span', { class: 'chats-tag' }, 'Enquiry') : el('span', { class: 'chats-sub' }, c.sub)
    const icon = wayIcon(c.last.way === 'text' ? 'text' : c.last.way === 'email' || c.last.way === 'form' ? 'email' : 'whatsapp')
    icon.classList.add(`chats-way-${c.last.way}`)
    const line = el('span', { class: 'chats-row-line' }, icon)
    if (mine) line.append(markIcon(markOf(c.last.state, !!c.last.read)))
    line.append(el('span', { class: 'chats-row-said' }, (c.last.body ?? WAY_WORDS[c.last.way] ?? '').replace(/\s+/g, ' ')))
    if (c.unread) line.append(el('span', { class: 'chats-unread' }, String(c.unread)))
    a.append(el('span', { class: `chats-avatar${c.kind === 'lead' ? ' chats-avatar-lead' : ''}`, 'aria-hidden': 'true' }, initials(c.name)),
      el('span', { class: 'chats-row-copy' }, el('span', { class: 'chats-row-top' }, el('strong', {}, c.name), tag, el('span', { class: 'chats-time' }, listTime(c.last.at))), line))
    a.addEventListener('click', () => choose({ kind: c.kind, id: c.id }))
    ul.append(el('li', {}, a))
  }
  panel.append(ul)
  return panel
}

function threadPanel(): HTMLElement {
  const panel = el('section', { class: 'chat-thread', 'aria-label': thread?.who.name ?? 'Conversation' })
  const head = el('div', { class: 'chat-head' })
  if (phoneLayout()) {
    const back = el('button', { type: 'button', class: 'chat-back', 'aria-label': 'Back to Chats' }, '‹')
    back.addEventListener('click', () => choose(null))
    head.append(back)
  }
  if (!thread) {
    head.append(el('strong', { class: 'chat-name' }, threadError ? "Couldn't open it" : 'Opening…'))
    panel.append(head)
    if (threadError) panel.append(el('p', { class: 'empty-note' }, threadError))
    return panel
  }
  const t = thread
  const who = el('div', { class: 'chat-who' }, el('strong', { class: 'chat-name' }, t.who.name), el('span', { class: 'chat-sub' }, [t.who.sub, t.who.address].filter(Boolean).join(' · ')))
  // What they owe on done visits not yet paid, into Owed (item 67): "Owes £18.00 · visit 7 Oct".
  if (t.owes && t.owes.pence > 0) {
    who.append(el('a', { class: 'chat-owes', href: '#/owed' }, `Owes ${money(t.owes.pence)} · ${t.owes.visits === 1 ? `visit ${shortDate(t.owes.oldest)}` : `${t.owes.visits} visits`} ›`))
  }
  head.append(el('span', { class: `chats-avatar${t.who.kind === 'lead' ? ' chats-avatar-lead' : ''}`, 'aria-hidden': 'true' }, initials(t.who.name)), who)
  const tel = telHref(t.who.phone)
  if (tel) head.append(el('a', { class: 'chat-head-btn', href: tel, 'aria-label': `Call ${t.who.name}` }, 'Call'))
  head.append(el('a', { class: 'chat-head-btn', href: t.who.kind === 'customer' ? `#/customer/${t.who.id}` : `#/new-jobs?lead=${t.who.id}` }, t.who.kind === 'customer' ? 'Their page' : 'Enquiry'))
  panel.append(head)

  const box = el('div', { class: 'chat-messages', role: 'log', 'aria-live': 'polite' })
  // Older than the latest 300: one tap loads the ones before (item 63).
  if (earlierMore ?? t.more) {
    const more = el('button', { type: 'button', class: 'link-button chat-earlier' }, 'Earlier messages')
    more.addEventListener('click', () => void loadEarlier(t, more))
    box.append(more)
  }
  let lastDay = ''
  const seen = new Set<number>()
  for (const m of [...earlier, ...t.messages, ...pending]) {
    if (m.id > 0 && seen.has(m.id)) continue
    seen.add(m.id)
    const d = when(m.at)
    // Messages brought in from before WhatsApp was connected say so on their day (item 9).
    const day = `${dayWords(d)}${m.source === 'history' ? ' · brought in from WhatsApp' : ''}`
    if (day !== lastDay) { box.append(el('div', { class: 'chat-day' }, day)); lastDay = day }
    const mine = m.direction === 'out'
    const meta = el('span', { class: 'chat-meta' }, [WAY_WORDS[m.way] ?? m.way, clock(d)].join(' · '))
    if (mine && m.source === 'phone') {
      // Typed on the work phone's WhatsApp Business app (item 8).
      meta.append(' ', markIcon('opened'), el('span', { class: 'chat-state chat-state-phone' }, ' Sent from your phone'))
    } else if (mine && m.unsure && m.chase_send) {
      // A payment text from Send all (0093) Roundly isn't sure went: never sent again by itself. The owner says whether it
      // arrived; "It didn't arrive" puts its reminder back on the visit, so the next one can go from Owed.
      meta.append(' ', markIcon('pending'), el('strong', { class: 'chat-state chat-state-unsure' }, ' Not sure it went'))
      const uid = m.chase_send
      for (const [label, arrived] of [['It arrived', true], ["It didn't arrive", false]] as const) {
        const b = el('button', { type: 'button', class: 'link-button chat-again' }, label) as HTMLButtonElement
        b.addEventListener('click', async () => {
          b.disabled = true
          try { await changeNow('POST', `/api/chase-sends/${uid}`, { arrived }) } catch (err) { sendError = (err as Error).message || "That didn't save. Try again." }
          await loadThread(true)
        })
        meta.append(' ', b)
      }
    } else if (mine && m.unsure) {
      // Roundly couldn't hear back whether it went (item 33): never sent again by itself; the owner checks the phone.
      meta.append(' ', markIcon('pending'), el('strong', { class: 'chat-state chat-state-unsure' }, " Not sure it went: check WhatsApp on the work phone"))
      const arrived = el('button', { type: 'button', class: 'link-button chat-again' }, 'It arrived')
      arrived.addEventListener('click', () => void markArrived(t, m.id))
      // A second tap, after the words "It may already have arrived", puts it back in the box to send as a new message.
      const again = el('button', { type: 'button', class: 'link-button chat-again' }, 'Send again anyway')
      const body = m.body ?? ''
      again.addEventListener('click', () => {
        if (again.dataset.armed !== '1') { again.dataset.armed = '1'; again.textContent = 'It may already have arrived. Tap again to send it again'; return }
        draft = body; picked = 'whatsapp'; sendKey = null; sendError = ''; render(); document.querySelector<HTMLTextAreaElement>('.chat-input')?.focus()
      })
      meta.append(' ', arrived, ' ', again)
    } else if (mine) {
      const mark = markOf(m.state, !!m.read_at, m.pending)
      meta.append(' ', markIcon(mark))
      // Words beside the mark only where a tick cannot say it.
      if (mark === 'opened' || mark === 'failed') meta.append(el('strong', { class: `chat-state chat-state-${mark}` }, ` ${MARK_WORDS[mark]}`))
      // A payment text from Send all that didn't go: sent again from Owed, which counts it on the visit (0093).
      if (mark === 'failed' && m.chase_send) meta.append(' ', el('a', { class: 'link-button chat-again', href: '#/owed' }, 'Send again from Owed'))
      else if (mark === 'failed' && m.body && (m.way === 'whatsapp' || m.way === 'text' || m.way === 'email')) {
        const again = el('button', { type: 'button', class: 'link-button chat-again' }, 'Try again')
        const body = m.body, w = m.way as Way
        again.addEventListener('click', () => { draft = body; picked = w; sendKey = null; sendError = ''; render(); document.querySelector<HTMLTextAreaElement>('.chat-input')?.focus() })
        meta.append(' ', again)
      }
    }
    box.append(el('div', { class: `chat-msg ${mine ? 'chat-mine' : 'chat-theirs'}${m.state === 'failed' ? ' chat-failed' : ''}` }, ...bubbleOf(t, m), meta))
  }
  if (!t.messages.length && !pending.length) box.append(el('p', { class: 'muted chat-none' }, 'Nothing yet. Say hello below.'))
  panel.append(box)
  panel.append(composer(t))
  return panel
}

// A message's bubble: its words, and a customer's photo or voice note as WhatsApp sent it (item 17), with a voice note
// written out under it (Liam, 21 Oct 2026: "just a note so we know it's a voice note in case it comes out wrong").
export function bubbleOf(t: Thread, m: Msg): HTMLElement[] {
  const first = t.who.name.split(/\s+/)[0]
  const words = m.body ?? (m.way === 'call' ? 'A call' : '(no words kept)')
  const kind = m.media_type
  if (!kind) return [el('div', { class: 'chat-bubble' }, words)]
  const src = `/api/chats/media/${m.id}`
  const parts: HTMLElement[] = []
  if (m.media_state === 'kept' && (kind === 'image' || kind === 'sticker')) {
    const img = el('img', { class: 'chat-photo', src, alt: `${kind === 'image' ? 'Photo' : 'Sticker'} from ${first}`, loading: 'lazy' })
    const link = el('a', { href: src, target: '_blank', rel: 'noopener', class: 'chat-photo-link' }, img)
    parts.push(el('div', { class: 'chat-bubble chat-media' }, link, ...(words !== 'Photo' && words !== 'Sticker' ? [el('div', { class: 'chat-caption' }, words)] : [])))
  } else if (m.media_state === 'kept' && kind === 'audio' && playsVoiceNotes()) {
    const audio = el('audio', { class: 'chat-audio', controls: '', preload: 'none', src, 'aria-label': `Voice note from ${first}` })
    parts.push(el('div', { class: 'chat-bubble chat-media' }, audio))
  } else if (m.media_state === 'kept' && kind === 'audio') {
    // WhatsApp's voice notes (Ogg) don't play on older iPhones (before iOS 18.4; Astra's code review): the words below
    // still show, and the phone's WhatsApp plays it.
    parts.push(el('div', { class: 'chat-bubble' }, el('div', { class: 'chat-media-note' }, 'Voice note: this phone plays it in WhatsApp, not here')))
  } else {
    const what = kind === 'audio' ? 'Voice note' : kind === 'image' ? 'Photo' : kind === 'video' ? 'Video' : kind === 'sticker' ? 'Sticker' : 'Document'
    const state = m.media_state === 'waiting' ? `${what} on its way from WhatsApp…` : `${what}: open WhatsApp on the phone to see it`
    parts.push(el('div', { class: 'chat-bubble' }, ...(words !== what ? [el('div', {}, words)] : []), el('div', { class: 'chat-media-note' }, state)))
  }
  if (m.words) parts.push(el('div', { class: 'chat-words' }, el('span', { class: 'chat-words-note' }, 'Written out from the voice note'), m.words))
  return parts
}

// Whether this phone or computer can play WhatsApp's voice notes (Ogg Opus) itself, asked once.
let oggPlays: boolean | null = null
function playsVoiceNotes(): boolean {
  if (oggPlays === null) {
    try { oggPlays = !!document.createElement('audio').canPlayType?.('audio/ogg; codecs=opus') } catch { oggPlays = false }
  }
  return oggPlays
}

// "Earlier messages": the 300 before the oldest shown.
async function loadEarlier(t: Thread, button: HTMLElement): Promise<void> {
  const oldest = [...earlier, ...t.messages].find((m) => m.id > 0)
  if (!oldest) return
  button.setAttribute('disabled', '')
  button.textContent = 'Getting earlier messages…'
  try {
    const data = await api<Thread>(`/api/chats/${t.who.kind}/${t.who.id}?before=${oldest.id}`)
    if (!thread || !sameChat(thread.who, t.who)) return
    earlier = [...data.messages, ...earlier]
    earlierMore = !!data.more
  } catch {
    button.removeAttribute('disabled')
    button.textContent = "Couldn't get them. Tap to try again"
    return
  }
  render()
}

// "Not sure it went" → the owner looked on the work phone and it arrived (item 33).
async function markArrived(t: Thread, messageId: number): Promise<void> {
  // Found before the save: switching chats meanwhile empties the earlier messages (Astra).
  const went = [...t.messages, ...earlier].find((m) => m.id === messageId)
  try {
    await changeNow('POST', `/api/chats/${t.who.kind}/${t.who.id}/whatsapp`, { action: 'arrived', message_id: messageId })
    // It went: when it is the held send itself (the same key, never just the same words), nothing is left to try again in
    // this chat, and its words in the box (unchanged) go with it, so Send can never send them a second time (Astra). An
    // edited draft stays.
    const here = chatKey({ kind: t.who.kind, id: t.who.id })
    const held = unsure.get(here)
    if (held && went?.send_key && went.send_key === held.key) {
      unsure.delete(here)
      if (open && chatKey(open) === here && draft === held.words) { draft = ''; sendKey = null; sendError = '' }
    }
  } catch (err) {
    sendError = (err as Error).message || "That didn't save. Try again."
  }
  await loadThread(true)
}

// The owner's word on stopping WhatsApps to this number (items 34, 56): Stop WhatsApps, Not a stop, Allow WhatsApps again.
async function stopChoice(t: Thread, action: 'stop' | 'not_a_stop' | 'allow'): Promise<void> {
  try {
    await changeNow('POST', `/api/chats/${t.who.kind}/${t.who.id}/whatsapp`, { action })
    picked = null
    // The app's list of stopped numbers is read again, so WhatsApp elsewhere (Messages, Owed, the Diary) follows at once.
    void loadMe()
  } catch (err) {
    sendError = (err as Error).message || "That didn't save. Try again."
  }
  await loadThread(true)
}

function stopPanel(t: Thread): HTMLElement | null {
  const stop = t.ways.waStop
  if (!stop) return null
  const first = t.who.name.split(/\s+/)[0]
  if (stop.state === 'held') {
    const yes = el('button', { type: 'button', class: 'btn-primary' }, 'Stop WhatsApps')
    yes.addEventListener('click', () => void stopChoice(t, 'stop'))
    const no = el('button', { type: 'button', class: 'btn-secondary' }, 'Not a stop')
    no.addEventListener('click', () => void stopChoice(t, 'not_a_stop'))
    return el('div', { class: 'chat-locked chat-stop' },
      el('p', {}, `Looks like ${first} asked to stop. Roundly holds WhatsApps to this number until you choose.`),
      el('div', { class: 'chat-locked-acts' }, yes, no))
  }
  const allow = el('button', { type: 'button', class: 'btn-secondary' }, 'Allow WhatsApps again')
  allow.addEventListener('click', () => void stopChoice(t, 'allow'))
  const said = stop.words && !stop.words.startsWith('by ') ? `${first} wrote ${/^\s*stop\s*$/i.test(stop.words) ? 'STOP' : `"${stop.words.slice(0, 80)}"`} on ${shortDate(stop.at.slice(0, 10))}.` : `You stopped WhatsApps to ${first} on ${shortDate(stop.at.slice(0, 10))}.`
  return el('div', { class: 'chat-locked chat-stop' },
    el('p', {}, `${said} Roundly won't send ${first} WhatsApps or open WhatsApp for this number. Text, email and Call still work.`),
    el('div', { class: 'chat-locked-acts' }, allow))
}

function composer(t: Thread): HTMLElement {
  const ways = waysFor(t)
  const way = picked && ways.includes(picked) ? picked : defaultWay(t)
  const form = el('form', { class: 'chat-compose' })
  const stop = stopPanel(t)
  if (stop) form.append(stop)
  // WhatsApp from the Roundly number, outside a day of their own WhatsApp: only approved wording (functions/lib/chat-send.ts).
  // Through Meta (0081) the box stays: Send opens WhatsApp on the phone with the words in instead (item 65).
  const waLocked = way === 'whatsapp' && t.ways.whatsapp && !t.ways.whatsappFree && t.ways.whatsappBy !== 'meta'
  const quick = el('div', { class: 'chat-quick' })
  for (const q of quickFills(t)) {
    const b = el('button', { type: 'button', class: 'chat-quick-btn' }, q.label)
    b.addEventListener('click', () => { draft = q.words; sendKey = null; render(); document.querySelector<HTMLTextAreaElement>('.chat-input')?.focus() })
    quick.append(b)
  }
  if (waLocked) form.append(lockedPanel(t))
  else form.append(quick)
  const input = el('textarea', { class: 'chat-input', rows: '1', placeholder: `Message ${t.who.name.split(/\s+/)[0]}`, 'aria-label': `Message ${t.who.name}` }) as HTMLTextAreaElement
  input.value = draft
  const fit = (): void => { input.style.height = 'auto'; input.style.height = `${Math.min(input.scrollHeight, 140)}px` }
  // Typing over words whose send wasn't confirmed keeps their key: Send then answers the first try, never a second message.
  input.addEventListener('input', () => { draft = input.value; if (!unsure.has(chatKey(open))) sendKey = null; fit() })
  // A quick fill of several lines opens the box to show them, not one line.
  if (draft.includes('\n') || draft.length > 40) requestAnimationFrame(() => { if (input.isConnected) fit() })
  const label = sendLabel(t, way)
  const send = el('button', { type: 'submit', class: 'chat-send' }, label) as HTMLButtonElement
  send.disabled = sending || waLocked
  if (!waLocked) form.append(el('div', { class: 'chat-row' }, input, send))
  // A text from the Roundly number longer than one text says how many it counts as, as it is written.
  const parts = el('p', { class: 'chat-parts', 'aria-live': 'polite' })
  const showParts = (): void => {
    const n = way === 'text' && roundlySends(t, 'text') ? textParts(draft) : 0
    parts.textContent = n > 1 ? `Long text: counts as ${n} texts (about ${n * 3}p).` : ''
    parts.hidden = n <= 1
  }
  showParts()
  input.addEventListener('input', showParts)
  if (!waLocked) form.append(parts)
  const pills = el('div', { class: 'chat-ways', role: 'group', 'aria-label': 'Send by' }, el('span', { class: 'muted' }, 'Sends by'))
  for (const w of ways) {
    const b = el('button', { type: 'button', class: `chat-way${w === way ? ' chat-way-on' : ''}`, 'aria-pressed': String(w === way) }, WAY_WORDS[w]!)
    b.addEventListener('click', () => { const shown = way; picked = w; sendError = ''; render(); rememberWay(t, w, shown) })
    pills.append(b)
  }
  // A stop asked another way (a call, in person): recorded here, for every conversation with this number (item 34).
  if (t.ways.whatsappBy === 'meta' && !t.ways.waStop && t.who.phone) {
    const stopIt = el('button', { type: 'button', class: 'link-button chat-stop-link' }, 'Stop WhatsApps from Roundly')
    stopIt.addEventListener('click', () => {
      if (stopIt.dataset.armed !== '1') { stopIt.dataset.armed = '1'; stopIt.textContent = `Tap again: no more WhatsApps from Roundly to ${t.who.name.split(/\s+/)[0]}`; return }
      void stopChoice(t, 'stop')
    })
    pills.append(stopIt)
  }
  form.append(pills)
  if (t.who.messageBy === 'none') form.append(el('p', { class: 'chat-error' }, "Their page says they don't want messages."))
  form.append(el('p', { class: 'chat-note' }, wayNote(t, way)))
  if (sendError) form.append(el('p', { class: 'chat-error', role: 'alert' }, sendError))
  form.addEventListener('submit', (e) => { e.preventDefault(); void sendNow(t, way) })
  input.addEventListener('keydown', (e) => {
    // Enter sends on a computer; Shift+Enter makes a new line. A phone's return key makes a new line.
    if (e.key === 'Enter' && !e.shiftKey && !phoneLayout()) { e.preventDefault(); void sendNow(t, way) }
  })
  return form
}

// A way picked here is kept for them (Liam, 15 Oct 2026: the chat "remembers Text for that person"): saved on the
// customer or the enquiry as their arrow saves it (contact-ui.ts saveWay), with Undo, so their next chat, chase and
// Messages card open the same way. Never over a customer's Don't message, and not before the chat has their version.
// A way that saved from the queue once signal returned (api.ts roundly:queued-landed): the open chat reads it, so its
// next pick and Undo start from what is stored.
if (typeof window !== 'undefined') window.addEventListener('roundly:queued-landed', (e) => {
  const d = (e as CustomEvent).detail as { method?: string; path?: string; row?: Record<string, unknown> } | undefined
  const m = d?.method === 'PATCH' ? /^\/api\/(leads|customers)\/(\d+)$/.exec(d.path ?? '') : null
  if (!m || !d?.row || !('message_by' in d.row) || !thread) return
  if (thread.who.kind !== (m[1] === 'leads' ? 'lead' : 'customer') || thread.who.id !== Number(m[2])) return
  thread.who.messageBy = (d.row.message_by as Thread['who']['messageBy']) ?? null
  if (typeof d.row.version === 'number') thread.who.version = d.row.version
})

// The way last asked for on each person from this phone, saved or still on its way, so a quick change back is sent
// after the first rather than skipped (Codex, 15 Oct 2026).
const askedWays = new Map<string, Thread['who']['messageBy']>()
// `shown` is the way the chat showed when it was tapped: tapping that same way again changes nothing, any other way is
// saved, whatever this phone last read (a change that landed from the queue may not have been read back yet; Codex).
function rememberWay(t: Thread, w: Way, shown: Way): void {
  const key = `${t.who.kind}:${t.who.id}`
  const route = t.who.kind === 'lead' ? 'leads' : 'customers'
  // A pick still on its way (sending, or waiting for signal) sits in the queue; with none, the chat's own reading is the
  // truth (another phone may have changed it since), whatever was asked here before.
  const waiting = (): boolean => getQueue().some((q) => q.method === 'PATCH' && q.path === `/api/${route}/${t.who.id}` && q.label === `How to message ${t.who.name}` && q.status !== 'conflict')
  if (!waiting()) askedWays.delete(key)
  const was = askedWays.has(key) ? askedWays.get(key)! : t.who.messageBy
  if (w === shown || t.who.messageBy === 'none' || t.who.version === undefined) return
  askedWays.set(key, w)
  const who: WayCustomer = { id: t.who.id, name: t.who.name, version: t.who.version, phone: t.who.phone, email: t.who.email, message_by: was, route }
  const landed = (row: Record<string, unknown> | null): boolean => {
    if (!row) return false
    if (thread && sameChat(thread.who, t.who)) {
      thread.who.messageBy = (row.message_by as Thread['who']['messageBy']) ?? null
      if (typeof row.version === 'number') thread.who.version = row.version
    }
    who.message_by = (row.message_by as string | null | undefined) ?? null
    if (typeof row.version === 'number') who.version = row.version
    return true
  }
  // Once nothing is on its way, the chat's own reading is the truth again (another phone may change it later).
  const settled = (asked: Thread['who']['messageBy']): void => { if (askedWays.get(key) === asked && !waiting()) askedWays.delete(key) }
  void saveWay(who, w).then((row) => {
    settled(w)
    if (!landed(row)) return
    offerUndo(`${t.who.name.split(/\s+/)[0]}: ${WAY_WORDS[w]} from now on`, () => {
      askedWays.set(key, was)
      void saveWay(who, was).then((back) => {
        settled(was)
        if (!landed(back)) return
        if (thread && sameChat(thread.who, t.who) && picked === w) { picked = null; render() }
      })
    })
  })
}

function wayNote(t: Thread, way: Way): string {
  const first = t.who.name.split(/\s+/)[0]
  // WhatsApp through Meta (0081): from the work number while the 24 hours are open, else the phone's WhatsApp.
  if (way === 'whatsapp' && t.ways.whatsappBy === 'meta') {
    if (t.ways.waProblem) return `WhatsApp from Roundly isn't working right now: ${t.ways.waProblem} This opens WhatsApp on your phone with your words in.`
    if (roundlySends(t, way) && t.ways.waUntil) return `Goes from your work number on WhatsApp. ${first} wrote recently, so you can write anything until ${untilWords(t.ways.waUntil)}.`
    if (t.ways.whatsapp) return `${first} hasn't written on WhatsApp in the last day, so WhatsApp only lets approved wording go from Roundly. This opens WhatsApp on your phone with your words in.`
  }
  if (roundlySends(t, way)) return way === 'email' ? `Emails ${first} from Roundly. Their reply comes back here.` : `Sends from your Roundly number. Their reply comes back here.`
  if (way === 'email') return `Opens your email with this written. Turn on Quotes by email in Extra features so replies come back here.`
  return `Opens your own ${way === 'text' ? 'Messages' : 'WhatsApp'} with this written: tap Send there. Their reply comes to your phone, not here.`
}

// "10:31 tomorrow", "18:05 today": when the 24 hours close, on this phone's clock.
function untilWords(at: string): string {
  const d = when(at)
  const day = dayWords(d)
  return `${clock(d)} ${day === 'Today' ? 'today' : dayKey(d) === dayKey(new Date(Date.now() + 86_400_000)) ? 'tomorrow' : day}`
}

// Outside WhatsApp's day: an enquiry's approved wording with one Send, or a text instead, or your own WhatsApp.
function lockedPanel(t: Thread): HTMLElement {
  const first = t.who.name.split(/\s+/)[0]
  const box = el('div', { class: 'chat-locked' },
    el('p', {}, `${first} hasn't written on WhatsApp in the last day, so WhatsApp only lets your Roundly number send approved wording. Once they reply, you can write anything for a day.`))
  const row = el('div', { class: 'chat-locked-acts' })
  const asText = el('button', { type: 'button', class: 'btn-primary' }, 'Write it as a text instead')
  asText.addEventListener('click', () => { picked = 'text'; render() })
  if (t.ways.text) row.append(asText)
  const own = el('a', { class: 'btn-secondary', href: whatsappHref(t.who.phone, draft || '') ?? '#', target: '_blank', rel: 'noopener' }, 'Use my own WhatsApp')
  own.addEventListener('click', () => { if (draft.trim()) { keepOpened(t, 'whatsapp', draft.trim()); draft = ''; render() } })
  row.append(own)
  box.append(row)
  if (t.who.kind === 'lead' && (t.ways.templates.first || t.ways.templates.nudge)) box.append(el('p', { class: 'muted' }, "Your approved first reply and nudge send from the enquiry's page on New jobs."))
  return box
}

function keepOpened(t: Thread, way: Way, body: string | null): void {
  void mutate({ label: `Kept: ${WAY_WORDS[way]} to ${t.who.name}`, method: 'POST', path: `/api/chats/${t.who.kind}/${t.who.id}/send`, body: { opened: true, way, body },
    onAck: () => { if (sameChat(open, t.who)) void loadThread(true); void loadList(true) } })
}

async function sendNow(t: Thread, way: Way): Promise<void> {
  const words = draft.trim()
  if (!words || sending) return
  sendError = ''
  if (!roundlySends(t, way)) {
    // The phone's own app, with the words written; kept as sent from it.
    const href = way === 'email' ? mailtoHref(t.who.email, '', words) : way === 'text' ? smsHref(t.who.phone, words, navigator.userAgent) : whatsappHref(t.who.phone, words)
    if (!href) { sendError = way === 'email' ? 'No email for them. Add one on their page.' : 'No mobile for them. Add one on their page.'; render(); return }
    if (!onAPhone()) {
      const form = document.querySelector<HTMLElement>('.chat-compose')
      if (!form || form.querySelector('.email-send-question')) return
      const link = el('a', { href })
      const gen = routeGen
      form.append(link)
      bindEmailSend(link, () => ({ href, sent: () => {
        if (routeGen !== gen || !sameChat(open, t.who)) return
        keepOpened(t, way, words)
        if (draft.trim() === words) draft = ''
        render()
      } }), () => link.remove())
      link.click()
      return
    }
    window.open?.(href, '_blank', 'noopener')
    keepOpened(t, way, words)
    draft = ''
    buzz('light')
    render()
    return
  }
  const gen = routeGen
  const here = chatKey({ kind: t.who.kind, id: t.who.id })
  const stillHere = (): boolean => gen === routeGen && chatKey(open) === here
  // An earlier send in this chat not yet confirmed goes again first, with ITS words and ITS key, so it can never go twice;
  // new words typed since stay in the box for the next Send (Astra's Stage 1 review).
  if (inflight.has(here)) { sendError = 'Your last message is still sending. Wait a moment, then tap Send.'; render(); return }
  const held = unsure.get(here)
  const retrying = !!held && held.words !== words
  const sendWords = held ? held.words : words
  const key = held?.key ?? crypto.randomUUID()
  inflight.add(here)
  sendKey = key
  const mine: Msg = { id: -Date.now(), direction: 'out', way, body: sendWords, state: 'opened', at: new Date().toISOString().slice(0, 19).replace('T', ' '), pending: true }
  pending = [...pending, mine]
  sending = true
  draft = retrying ? words : ''
  render()
  try {
    await changeNow('POST', `/api/chats/${t.who.kind}/${t.who.id}/send`, { way, body: sendWords }, key)
    inflight.delete(here)
    unsure.delete(here)
    if (!stillHere()) { sending = false; return }
    // Words put back in the box from this very attempt have now gone: never left there to go a second time.
    if (draft === sendWords && !retrying) draft = ''
    buzz('success')
    sendKey = null
    // Shown as sent until the conversation reloads with it in (the bubble never vanishes if that reload fails).
    mine.pending = false
    mine.state = 'sent'
    sending = false
    await loadThread(true)
    pending = pending.filter((m) => m !== mine)
    render()
    void loadList(true)
  } catch (err) {
    const e = err as ApiError
    // No answer, or WhatsApp's own "not sure it went": kept with its own chat, whichever chat is open now. A clear refusal
    // frees the chat for a new message.
    inflight.delete(here)
    // No signal: kept with its own chat, so the next Send there tries the same words with the same key. Anything else
    // (a clear refusal, or WhatsApp's "not sure it went", which the chat itself then shows with its own choices) frees it.
    if (e.status === 0) unsure.set(here, { words: sendWords, key })
    else unsure.delete(here)
    if (!stillHere()) { sending = false; return }
    pending = pending.filter((m) => m !== mine)
    sending = false
    draft = retrying ? words : sendWords
    // No answer from the server: it may have gone. The same key goes with the next tap, so it can never go twice.
    if (e.status === 0) sendError = "We couldn't confirm it went (no signal). Tap Send again when you're connected: it won't go twice."
    // Roundly couldn't hear back from WhatsApp (0081, item 33): the message stays in the chat as "Not sure it went", the
    // words are not put back in the box, and the same key stays, so a second tap answers the same and never sends again.
    else if (e.code === 'send_unknown') { draft = retrying ? words : ''; sendError = e.message || "We couldn't confirm it went. Check WhatsApp on the work phone." }
    else { sendKey = null; sendError = e.message || "It didn't go. Try again." }
    if (e.code === 'needs_template' && thread) thread.ways.whatsappFree = false
    if (e.code === 'send_unknown' || e.code === 'stopped' || e.code === 'not_ready') void loadThread(true)
    render()
  }
}
